import { cn } from '@/shadcn-ui/utils';
import { LoaderButton } from '@/common/components/loader-button';
import { PackageSummary } from '@/catalog/hooks/use-package-summary';
import { Skeleton } from '@/shadcn-ui/skeleton';
import { useBreakpoint } from '@/common/hooks/use-breakpoint';
import { withPageContainerStyles } from '@/common/components/page-layout';
import Spacer from '@/common/components/spacer';
import * as gtag from '@/common/utils/gtags.client';
import EstimatedPricing from './estimated-pricing';

export interface PDPStickyBarProps {
  packageSummary?: PackageSummary | undefined;
  isLoading?: boolean;
  fullstoryContinueEventDelegate?: () => void;
  PRISTINE_HERO_PDP?: boolean;
  handleSavingsClick?: () => void;
}

export default function PDPStickyBar({
  packageSummary,
  isLoading,
  fullstoryContinueEventDelegate,
  PRISTINE_HERO_PDP = false,
  handleSavingsClick,
}: PDPStickyBarProps) {
  const breakpoint = useBreakpoint();
  const handleSubmitEvent = () => {
    gtag.event('vehicle_details_continue');
    fullstoryContinueEventDelegate?.();
  };

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 z-10 flex min-w-full translate-y-0 transform items-center justify-center bg-flexcar-neutral100 py-4 shadow-shadow30 transition-transform duration-800 ease-in-out',
        {
          'translate-y-full': !packageSummary,
          'p-[16px] shadow-stickyBarShadow lg:hidden': PRISTINE_HERO_PDP,
        }
      )}
    >
      <div
        className={withPageContainerStyles(
          'w-full max-w-[1440px] flex-row items-center justify-end gap-6',
          {
            'justify-between p-0': PRISTINE_HERO_PDP,
          }
        )}
      >
        <div className="flex flex-col">
          {isLoading ? (
            <>
              <Skeleton className="h-4 w-[220px]" />
              <Spacer variant="height8" />
              <Skeleton className="h-4 w-[200px] self-end" />
            </>
          ) : PRISTINE_HERO_PDP ? (
            <>
              <EstimatedPricing
                estimatedMonthlyCostCopy={packageSummary?.estimatedMonthlyCostCopy || ''}
                estimatedMonthlySavingsCopy={packageSummary?.estimatedSavingsCopy}
                onClickDelegate={handleSavingsClick}
              />
            </>
          ) : (
            <>
              <p className="body2 whitespace-nowrap text-right">
                {breakpoint === 'mobile'
                  ? `Monthly cost`
                  : `${packageSummary?.mileagePlanName} • ${packageSummary?.insurancePlanName}`}
              </p>
              <p className="body2-strong whitespace-nowrap text-right">
                {breakpoint === 'mobile'
                  ? packageSummary?.estimatedMonthlyCostCopy
                  : `Est. monthly cost: ${packageSummary?.estimatedMonthlyCostCopy}`}
              </p>
            </>
          )}
        </div>

        <LoaderButton
          type="submit"
          name="intent"
          value="plan-with-insurance"
          className={cn('max-w-[232px] grow sm:min-w-[232px] sm:grow-0', {
            'h-[48px] max-w-[280px] rounded-full px-[48px] py-[12px] ring-0 hover:bg-flexcar-neutral20 hover:ring-0 focus:ring-0 sm:min-w-[280px]':
              PRISTINE_HERO_PDP,
          })}
          disabled={isLoading}
          onClick={handleSubmitEvent}
        >
          Continue
        </LoaderButton>
      </div>
    </div>
  );
}
